import React from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Printer, DownloadSimple, BookOpen } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const H2 = ({ children, id }) => (
  <h2 id={id} className="text-2xl md:text-3xl font-bold tracking-tight text-text-primary mt-10 mb-3 pb-2 border-b border-border-default">
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="text-lg md:text-xl font-semibold text-text-primary mt-6 mb-2">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-text-secondary leading-relaxed mb-3">{children}</p>
);

const Code = ({ children }) => (
  <code className="px-1.5 py-0.5 rounded bg-surface font-mono text-sm text-accent-brand">{children}</code>
);

const UL = ({ items }) => (
  <ul className="list-disc list-inside text-text-secondary space-y-1">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

const OL = ({ items }) => (
  <ol className="list-decimal list-inside text-text-secondary space-y-1">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ol>
);

const FAQ = ({ q, a }) => (
  <div>
    <p className="font-semibold text-text-primary">Q: {q}</p>
    <p className="text-text-secondary">{a}</p>
  </div>
);

const RoleTable = () => (
  <div className="overflow-x-auto my-4">
    <table className="w-full text-sm border border-border-default">
      <thead className="bg-surface">
        <tr>
          <th className="p-3 text-left font-semibold text-text-primary border-b border-border-default">Role</th>
          <th className="p-3 text-left font-semibold text-text-primary border-b border-border-default">Owns</th>
          <th className="p-3 text-left font-semibold text-text-primary border-b border-border-default">Cannot do</th>
        </tr>
      </thead>
      <tbody className="text-text-secondary">
        {[
          ['Admin', 'User accounts & roles, view all reset links', 'Participate in procurement workflow'],
          ['Employee', 'Raise requisitions (manual or AI import)', "See other employees' data"],
          ['Lab Manager', 'Vendor directory, RFQ emails, quotations, release POs, send PO to vendor, record delivery', 'Pick vendors, create POs'],
          ['Operations Manager', 'Pick vendors (flexible quote rule), negotiations, project codes, release/send POs, record delivery', "Create POs (finance's job)"],
          ['Finance', 'Create Purchase Orders + expected delivery date', 'Pick vendors, record actual delivery'],
        ].map(([r, own, no]) => (
          <tr key={r} className="border-b border-border-default">
            <td className="p-3 font-semibold text-text-primary">{r}</td>
            <td className="p-3">{own}</td>
            <td className="p-3">{no}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CredsTable = () => (
  <div className="overflow-x-auto my-4">
    <table className="w-full text-sm border border-border-default">
      <thead className="bg-surface">
        <tr>
          <th className="p-3 text-left font-semibold text-text-primary border-b border-border-default">Role</th>
          <th className="p-3 text-left font-semibold text-text-primary border-b border-border-default">Email</th>
          <th className="p-3 text-left font-semibold text-text-primary border-b border-border-default">Password</th>
        </tr>
      </thead>
      <tbody className="text-text-secondary font-mono text-xs">
        {[
          ['Admin', 'admin@biotech.com', 'admin123'],
          ['Employee', 'employee@biotech.com', 'emp123'],
          ['Lab Manager', 'labmanager@biotech.com', 'lab123'],
          ['Ops Manager', 'operations@biotech.com', 'ops123'],
          ['Finance', 'finance@biotech.com', 'fin123'],
        ].map(([r, e, p]) => (
          <tr key={e} className="border-b border-border-default">
            <td className="p-3">{r}</td>
            <td className="p-3">{e}</td>
            <td className="p-3">{p}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TrainingGuide = () => {
  const handlePrint = () => window.print();
  const handleDownloadMd = () => {
    window.open(`${API}/training-guide/markdown`, '_blank');
  };

  return (
    <DashboardLayout>
      <style>{`
        @media print {
          header, nav, [data-testid="notifications-popover"], .no-print { display: none !important; }
          body { background: white !important; }
          main, .training-content { padding: 0 !important; }
          h1, h2, h3 { break-after: avoid; }
          table { break-inside: avoid; }
        }
      `}</style>

      <div className="training-content max-w-4xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap no-print" data-testid="training-header">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-accent-brand-subtle flex items-center justify-center">
              <BookOpen size={24} weight="duotone" className="text-accent-brand" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-text-primary">
                Training Guide
              </h1>
              <p className="text-sm text-text-secondary">Everything a new hire needs to know — v2.0 · June 2026</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleDownloadMd} data-testid="download-md-button">
              <DownloadSimple size={16} className="mr-1.5" /> Download Markdown
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-accent-brand hover:bg-accent-brand-hover text-white"
              data-testid="print-pdf-button"
            >
              <Printer size={16} className="mr-1.5" /> Print / Save as PDF
            </Button>
          </div>
        </div>

        <div className="bg-white border border-border-default rounded p-6 md:p-10 space-y-2">
          <p className="text-xs uppercase tracking-widest text-text-tertiary">Metastop Reagent Procurement System</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-text-primary">
            End-to-end training guide
          </h1>
          <p className="text-text-secondary">Version 2.0 · June 2026 · Currency: Indian Rupee (₹)</p>

          <H2 id="overview">1. System Overview</H2>
          <P>
            The app automates reagent procurement from request through quotes, negotiation, PO, dispatch, delivery and performance tracking, across 5 roles:
          </P>
          <pre className="bg-surface border border-border-default rounded p-4 text-xs md:text-sm overflow-x-auto text-text-primary">{`   EMPLOYEE       LAB MANAGER          OPS MANAGER          FINANCE       OPS / LAB          OPS / LAB
Requisition → Quotes (RFQ / AI / → Vendor pick        → Purchase  → Release PO +   → Record delivery
               manual entry)        (± negotiation,      Order       email PO PDF     (locked once
                                    14-day accumulation)             to vendor        recorded)`}</pre>
          <H3>Smart optimizations built in</H3>
          <UL items={[
            <span key="1"><strong>14-day accumulation window</strong> — approvals to the same vendor batch into one PO (saves shipping).</span>,
            <span key="2"><strong>AI file parsing</strong> — upload a PDF/image; requisitions or quotations are auto-filled (Gemini 3 Flash).</span>,
            <span key="3"><strong>RFQ emails + auto-reminders</strong> — quote requests go straight to vendors; unanswered RFQs get nudged automatically after 3 days.</span>,
            <span key="4"><strong>Flexible quote rule</strong> — 2 quotations recommended, not mandatory; Ops Manager can proceed with 1.</span>,
            <span key="5"><strong>Optional negotiation</strong> — Lab Manager, Ops Manager and/or Finance can jointly negotiate; Ops uploads the final negotiated invoice.</span>,
            <span key="6"><strong>Cloud storage</strong> — uploaded PDFs (quotations, negotiated invoices) live in cloud object storage.</span>,
          ]} />

          <H2 id="roles">2. The 5 Roles</H2>
          <RoleTable />

          <H2 id="workflow">3. End-to-End Workflow</H2>
          <H3>Stage 1 — Requisition (Employee)</H3>
          <P>Employee raises a request with reagent name, preferred vendor, catalogue #, and project code. Status: <Code>pending_quotation</Code>. Lab Managers are notified. Duplicate detection warns if the same item is already in-flight — you can <strong>join</strong> an existing request as a co-requester instead of duplicating it.</P>
          <H3>Stage 2 — Quotations (Lab Manager)</H3>
          <P>Three ways to get quotes in:</P>
          <OL items={[
            <span key="1"><strong>RFQ emails</strong> — click RFQ on a requisition, tick one or more vendors from the directory; each gets its own quote-request email (vendors never see each other). Replies come to your inbox. The row shows <strong>"N/M RFQs answered"</strong>.</span>,
            <span key="2"><strong>AI PDF import</strong> — upload the vendor's quotation PDF; rows are auto-extracted and matched.</span>,
            <span key="3"><strong>Manual entry</strong> — vendor, price (₹), delivery timeline, payment terms.</span>,
          ]} />
          <P>With ≥ 2 quotes the requisition auto-advances to <Code>quotations_added</Code>. With just 1 quote, the Ops Manager can override (Stage 3).</P>
          <H3>Stage 3 — Vendor Selection (Ops Manager)</H3>
          <UL items={[
            <span key="1"><strong>Awaiting Quotations</strong> section: requisitions with ≥ 1 quote can be moved to selection early via <strong>Proceed to Selection</strong> — two quotes are not mandatory.</span>,
            <span key="2"><strong>Optional negotiation</strong> before approving (see Section 6).</span>,
            <span key="3">Approve the winning vendor → item joins the vendor's 14-day accumulation bucket.</span>,
          ]} />
          <H3>Stage 4 — Purchase Order (Finance)</H3>
          <P>When the bucket closes (or Ops finalizes early), Finance creates the PO with a PO number and <strong>expected delivery date</strong>. Status: <Code>po_created</Code>. Ops + Lab Managers are notified.</P>
          <H3>Stage 5 — PO Release &amp; Dispatch (Ops OR Lab Manager)</H3>
          <P>On the <strong>POs tab</strong> (both dashboards): 1) <strong>Release PO</strong> — final human check. 2) <strong>Send to Vendor</strong> — emails the PO PDF to all the vendor's contacts (only after release). Row then shows "Sent {'{date}'}" with a Resend option.</P>
          <H3>Stage 6 — Delivery (Ops OR Lab Manager)</H3>
          <P>Whoever physically receives the goods clicks <strong>Record Delivery</strong>: actual date, on-time/late, 1–5 star quality rating. <strong>Once recorded by either manager, it locks</strong> — everyone else sees a green "Delivered" state. Requesters get a delivery email; the other manager gets an in-app "✅ Delivered" alert. Finance sees a read-only indicator. Feeds the Vendor Performance dashboard.</P>

          <H2 id="role-guides">4. Detailed Role Guides</H2>

          <H3>4.1 Admin</H3>
          <UL items={[
            'Create user accounts (New User → email + name + password + role).',
            'View all users in the table.',
            'Copy password reset links from "Pending Password Reset Links" if a user can\'t get their reset email (30-min expiry).',
          ]} />

          <H3>4.2 Employee — My Requisitions</H3>
          <P><strong>Manual entry:</strong> New Requisition → reagent, vendor, catalogue #, project code → Submit.</P>
          <P><strong>AI Import:</strong> Import from File (AI) → PDF/image or Take Photo → review auto-filled table (amber = low AI confidence) → assign project code → Create Requisitions.</P>
          <P><strong>Duplicates:</strong> if a matching in-flight request exists you'll be warned — join it as a co-requester to receive all its updates. Filter by keyword + status + date; export requisitions as PDF.</P>

          <H3>4.3 Lab Manager — Quotation Management</H3>
          <UL items={[
            <span key="1"><strong>Vendors page</strong> (nav): maintain the vendor directory (Section 5).</span>,
            <span key="2"><strong>RFQ button</strong> per pending requisition: multi-select vendors → send quote requests; track "N/M RFQs answered".</span>,
            <span key="3"><strong>RFQ Reminders button</strong>: manually sweep unanswered RFQs (also runs automatically every 6 hours).</span>,
            <span key="4"><strong>Import Quotation PDF</strong> (AI) or manual quote entry.</span>,
            <span key="5"><strong>Purchase Orders tab:</strong> Release PO, Send to Vendor (PO PDF email), Record Delivery.</span>,
          ]} />

          <H3>4.4 Operations Manager — 5 tabs</H3>
          <UL items={[
            <span key="1"><strong>Approvals:</strong> Review &amp; Approve vendor; <strong>Awaiting Quotations</strong> table with Proceed to Selection override (≥ 1 quote).</span>,
            <span key="2"><strong>Negotiation:</strong> per quotation — Negotiate → pick the team → Upload Final Quote (negotiated invoice PDF + final price).</span>,
            <span key="3"><strong>Vendor Orders:</strong> accumulation buckets with 14-day countdown; Finalize Order to send to Finance early.</span>,
            <span key="4"><strong>POs:</strong> Release, Send to Vendor, Record Delivery.</span>,
            <span key="5"><strong>Project Codes</strong> tab and <strong>Vendors</strong> page (shared with Lab Manager).</span>,
            <span key="6"><strong>/analytics:</strong> full dashboard incl. the <strong>Negotiation Savings</strong> card (total ₹ saved, count, avg %).</span>,
          ]} />

          <H3>4.5 Finance — 3 tabs</H3>
          <UL items={[
            <span key="1"><strong>Ready for PO:</strong> vendor buckets from Ops → Create PO (PO number + expected delivery date).</span>,
            <span key="2"><strong>Created POs:</strong> all POs with filters; read-only "Delivered {'{date}'}" indicator once Lab/Ops record delivery; export PDF.</span>,
            <span key="3"><strong>Processed:</strong> historical completed POs.</span>,
            <span key="4">Note: Finance no longer records deliveries — that moved to Lab/Ops managers (the people who physically receive goods).</span>,
          ]} />

          <H2 id="vendors">5. Vendor Directory, RFQs &amp; Auto-Reminders</H2>
          <UL items={[
            <span key="1"><strong>Directory</strong> (<Code>/vendors</Code>, Lab + Ops managers): vendor name + email contacts. At least 2 emails recommended (warning with only 1, but you can still save). Duplicate names blocked.</span>,
            <span key="2"><strong>RFQ emails:</strong> Lab Manager selects one or more vendors per requisition; each vendor gets a separate email to <strong>all</strong> their contacts asking for price (INR), delivery timeline, payment terms, quote validity. Reply-to is the lab manager's own email.</span>,
            <span key="3"><strong>Auto-reminders:</strong> unanswered RFQs get a polite nudge after <strong>3 days</strong> (once per RFQ) + an in-app alert to lab managers. Runs every 6 hours; manual trigger available.</span>,
            <span key="4"><strong>PO dispatch</strong> uses the same directory: Send to Vendor emails the PO PDF to all contacts.</span>,
          ]} />

          <H2 id="negotiation">6. Negotiation Workflow (optional)</H2>
          <OL items={[
            'Ops Manager clicks Negotiate on a quotation and selects the negotiating team — any combination of Lab Manager, Operations Manager, Finance. Selected people are notified (email + in-app).',
            'The team negotiates with the vendor offline.',
            'Ops Manager (final call) clicks Upload Final Quote: final agreed price (₹) + the negotiated invoice PDF.',
            'The quotation updates to the final price (original shown struck-through), the badge shows the team, and the invoice is downloadable.',
            'Negotiation Savings on the Analytics dashboard totals every ₹ saved.',
          ]} />
          <P>Negotiation is never mandatory — Ops can approve a vendor straight from the original quotes.</P>

          <H2 id="cross-cutting">7. Cross-Cutting Features</H2>
          <UL items={[
            <span key="0"><strong>Currency: INR</strong> — ₹ across the app and emails, "Rs." in generated PDFs.</span>,
            <span key="1"><strong>Search &amp; filters</strong> on every dashboard (keyword + status + date range).</span>,
            <span key="2"><strong>Notifications</strong> — bell icon for in-app alerts; live emails via Resend (verified domain <Code>updates.mestastop.com</Code>); <strong>global email toggle</strong> in the bell popover (in-app alerts always stay on).</span>,
            <span key="3"><strong>Change Password</strong> — Key icon next to logout, on every dashboard.</span>,
            <span key="4"><strong>Audit Trail</strong> (<Code>/audit-trail</Code>) — every user sees their own actions (incl. RFQs, negotiations, deliveries).</span>,
            <span key="5"><strong>Analytics</strong> (<Code>/analytics</Code>) — admin &amp; ops — KPIs, Negotiation Savings, status pie, top employees, top vendors, recent activity.</span>,
            <span key="6"><strong>Vendor Performance</strong> (<Code>/vendor-performance</Code>) — RadarChart: Reliability / Price Edge / Fulfillment / Rating.</span>,
            <span key="7"><strong>Forgot Password</strong> — 30-min reset links delivered by email; admin can copy links as a fallback.</span>,
            <span key="8"><strong>PDF exports</strong> — requisitions and POs as branded PDFs.</span>,
            <span key="9"><strong>Cloud storage</strong> — uploaded quotation and negotiated-invoice PDFs are stored in cloud object storage (survive restarts/redeploys).</span>,
          ]} />

          <H2 id="setup">8. First-Time Setup Checklist</H2>
          <P><strong>Production first login:</strong> the system auto-creates the initial admin <Code>arnab@mestastop.com / admin123</Code> on a fresh database.</P>
          <H3>Admin (first login)</H3>
          <OL items={[
            'Change your password immediately (Key icon next to logout)',
            'Create teammate accounts with correct roles',
            'Have Ops Manager create the project codes your labs use',
            'Have Lab/Ops managers populate the Vendor Directory (name + at least 2 emails per vendor)',
          ]} />
          <H3>Every new user</H3>
          <OL items={[
            'Log in with admin-provided credentials',
            'Change your password (Key icon)',
            'Read the section above for your role',
          ]} />

          <H2 id="faq">9. FAQ</H2>
          <div className="space-y-3">
            <FAQ q="Reset email never arrived." a="Emails are live, so check spam first; otherwise ask Admin to copy the reset link from their dashboard (30-min expiry)." />
            <FAQ q="We only got one quotation. Are we stuck?" a="No. Ops Manager can use Proceed to Selection in the Awaiting Quotations section with a single quote." />
            <FAQ q="Who emails the vendor for quotes?" a="The Lab Manager, via the RFQ button — no personal inbox needed. Vendor replies go to the lab manager's email." />
            <FAQ q="Vendor hasn't replied to our RFQ." a="After 3 days the system automatically reminds them (once) and alerts lab managers. You can also click RFQ Reminders anytime." />
            <FAQ q="Who records the delivery?" a="Lab Manager or Ops Manager — whoever receives the goods. It locks after the first entry; Finance sees a read-only indicator." />
            <FAQ q="Recorded a wrong delivery date." a="Delivery is locked once recorded; contact Admin — everything is audit-logged." />
            <FAQ q="14-day timer running — can we ship earlier?" a="Yes. Ops Manager clicks Finalize Order in the Vendor Orders tab." />
            <FAQ q="Where do quotation PDFs live?" a="Cloud object storage, linked to the quotation for the audit trail; downloadable anytime." />
            <FAQ q="Too many emails!" a='Open the bell popover and switch off "Email notifications" — in-app alerts keep working. (Security emails like password resets always send.)' />
            <FAQ q="Mobile support?" a="Yes. Employees can use Take Photo to snap a bottle label with the rear camera." />
          </div>

          <H2 id="creds">Demo Credentials (preview only)</H2>
          <CredsTable />
          <P><strong>Production:</strong> first admin is <Code>arnab@mestastop.com / admin123</Code> — change the password on first login.</P>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainingGuide;
